// @ts-strict-ignore
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { FormState } from '../types';
import {
  Box, Checkbox, Icon, IconName,
  TextArea,
  Tooltip,
} from '@usealloy/component-library';
import { parseBooleanString } from '~/dashboard/views/workflows/service/util';
import { JQ_TOOLTIP_CONTENT } from './constants';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { updateAction } from '~/dashboard/views/workflows/store/redux/slice';
import { selectFocusedAction, selectWorkflowType } from '~/dashboard/views/workflows/store/redux/selectors';
import type { Action } from '~/dashboard/views/workflows/store/engine/types';
import { WORKFLOW_TYPES } from '~/constants';


export default function Footer() {
  const {
    control,
    watch,
    register,
    formState,
  } = useFormContext<FormState>();

  const dispatch = useAppDispatch();

  const workflowType = useAppSelector(selectWorkflowType);
  const focusedAction = useAppSelector(selectFocusedAction);
  const useJq = String(watch('useJq'));
  const jqFunction = watch('jqFunction');


  const debouncedUpdateAction = useMemo(
    () => (
      debounce((args: Parameters<typeof updateAction>['0']) => dispatch(updateAction(args)), 250)
    ), [dispatch]);


  useEffect(() => {
    if (formState.isDirty) {
      const nextOutputState: Action['output'] = {
        ...focusedAction.output,
        useJq,
        jqFunction,
      };

      debouncedUpdateAction({
        path: `${focusedAction.publicId}.output`,
        value: nextOutputState,
      });
    }
  }, [debouncedUpdateAction, dispatch, focusedAction, formState.isDirty, jqFunction, useJq],
  );

  return workflowType !== WORKFLOW_TYPES.PORTFOLIO && (
    <Box
      alignItems="center"
      direction="column"
      gap={2}
      style={{ padding: 'var(--cl-space-4) var(--cl-space-6)' }}
    >
      <Box
        justifyContent="flex-start"
      >
        <Controller
          control={control}
          name="useJq"
          render={({ field: { onChange, value } }) => (
            <Box justifyContent="flex-start">
              <Checkbox
                checked={parseBooleanString(value)}
                label="Parse expression with JQ?"
                onChange={onChange}
              />
              <Tooltip
                side="top"
                content={JQ_TOOLTIP_CONTENT}
              >
                <Icon
                  icon={IconName.Info}
                  size="small"
                />
              </Tooltip>
            </Box>
          )}
        />
      </Box>

      {parseBooleanString(useJq) && (
        <Box
          style={{ marginTop: 'var(--cl-space-2)' }}
        >
          <TextArea
            {...register('jqFunction')}
            label="JQ Formula"
            placeholder=".expression |"
            rows={3}
            showLineNumbers
          />
        </Box>
      )}

    </Box>
  );
}
